import dynamic from 'next/dynamic';

import React, { useRef, useState, useEffect, Fragment } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore from "swiper";
import { Navigation } from "swiper/modules";
import "swiper/swiper-bundle.css";
import styles from "./swiperButton.module.css"; // Import the CSS module
import { getGamesFromWebsiteCollection } from "@/utils/firebaseFunctions";

const GameItem = dynamic(() => import('@/components/games-page/game-item'), { ssr: false });
const Icon = dynamic(() => import('@/components/ui/Icon'), { ssr: false });
const LogoLoading = dynamic(() => import('@/components/ui/LogoLoading'));

// Initialize Swiper modules
SwiperCore.use([Navigation]);

const Games = ({ osTypes }) => {
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const [swiper, setSwiper] = useState(null);
  const [games, setGames] = useState([]);

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    if (games.length === 0) {
      const fetchGames = async () => {
        const games = await getGamesFromWebsiteCollection(osTypes);

        setGames(games);
        setIsLoading(false);
      };

      fetchGames();
    }
  }, []);

  useEffect(() => {
    if (prevRef.current && prevRef.current && swiper) {
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, [swiper]);

  // Define priority games
  const priorityGames = ['game-web-match', 'game-web-pipes', 'game-web-pop', 'game-web-bounce', 'game-web-invaders']; // Replace with your specific game titles or IDs

  // Sort games: first the priority games, then the rest
  const sortedGames = [
    ...games.filter((game) => priorityGames.includes(game.slug.current)),
    ...games.filter((game) => !priorityGames.includes(game.slug.current)),
  ];

  // Loading Indicator JSX
  const LoadingIndicator = () => (
    <div
      className="loading-placeholder-responsive-carousel"
      style={{
        backgroundColor: "transparent",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <LogoLoading />
    </div>
  );

  return (
    <Fragment>
      {isLoading && games && games.length > 0 ? (
        <LoadingIndicator />
      ) : (
        <Fragment>
          <div
            className={
              osTypes.includes("pc")
                ? "pc-games"
                : osTypes.includes("web")
                  ? "web-games"
                  : "mobile-games"
            }
          >
            <h4 className="font-semibold dark:text-white lg:text-2xl text-xl capitalize text-zinc-900 px-4">
              {osTypes.includes("pc") ? (
                "PC Games"
              ) : (
                <>
                  {osTypes.includes("web")
                    ? "WebPlay / Telegram"
                    : "Mobile Games"}
                </>
              )}
            </h4>

            <div className="game-area relative" id="games">
              <div
                className={`swiper-button-prev ${styles["custom-swiper-button-left"]}`}
                ref={prevRef}
                onClick={() => swiper?.slidePrev()}
              >
                <Icon icon="fluent:ios-arrow-left-24-filled" />
              </div>
              <div
                className={`swiper-button-next ${styles["custom-swiper-button-right"]}`}
                ref={nextRef}
                onClick={() => swiper?.slideNext()}
              >
                <Icon icon="fluent:ios-arrow-right-24-filled" />
              </div>

              <Swiper
                onSwiper={setSwiper}
                navigation={{
                  prevEl: prevRef.current,
                  nextEl: nextRef.current,
                }}
                onInit={(swiper) => {
                  swiper.params.navigation.prevEl = prevRef.current;
                  swiper.params.navigation.nextEl = nextRef.current;
                  swiper.navigation.init();
                  swiper.navigation.update();
                }}
                breakpoints={{
                  1280: { slidesPerView: 4, spaceBetween: 24 },
                  992: { slidesPerView: 4, spaceBetween: 24 },
                  768: { slidesPerView: 3, spaceBetween: 24 },
                  0: { slidesPerView: 2, spaceBetween: 12 },
                }}
                updateOnWindowResize
                observer
                observeParents
                className="gamesSwiper !py-8 md:!px-4 !px-2"
              >
                {sortedGames.map((game, i) => {
                  return (
                    <SwiperSlide key={i}>
                      <GameItem game={game} />
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default Games;
